import { useTranslation } from "react-i18next";
import { cellChoices } from "@/lib/reference-data-edit";
import type { ReferenceDataColumn } from "@/types/reference-data";

/** One editable cell. A column restricted to a closed set of values (its CHECK
 *  constraint, or `boolean`) gets a select, so the accepted values are visible
 *  instead of guessed — a wrong guess is rejected on staging. Everything else
 *  stays free text. `className` comes from the caller so the batch grid and the
 *  row dialog can each keep their own styling.
 *
 *  Shared by both editors on purpose: the `outsideDomain` rule below is subtle
 *  enough that a second implementation would drift.
 */
export function CellEditor({
  column,
  label,
  value,
  className,
  placeholder,
  id,
  onChange,
}: {
  column: ReferenceDataColumn | undefined;
  label: string;
  value: string;
  className: string;
  placeholder: string;
  /** Set when a visible <label htmlFor> points at the control (row dialog). */
  id?: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const choices = cellChoices(column, value);

  if (!choices) {
    return (
      <input
        id={id}
        aria-label={label}
        className={className}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <select
      id={id}
      aria-label={label}
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {/* Empty = "not provided": the row keeps its column default on create, and
          an update leaves the column untouched (empty cells are dropped). */}
      <option value="">{placeholder || "—"}</option>
      {choices.options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
      {/* A value the domain no longer covers (several CHECKs were tightened by
          later migrations) stays selectable — dropping it would rewrite the cell
          just by opening the editor. */}
      {choices.outsideDomain !== null && (
        <option value={choices.outsideDomain}>
          {choices.outsideDomain} — {t("not an allowed value")}
        </option>
      )}
    </select>
  );
}
